import useApiClient from 'hooks/use-api-client';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import isString from 'lodash/isString';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { returnData } from 'services/api-client/client';
import type { EnvironmentApi } from '@env0/environment-service/api';
import type { Environment } from 'types/api.types';

export const useGetEnvironment = (environmentId?: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment', environmentId],
    queryFn: () => apiClient.environments.getEnvironment(environmentId!),
    enabled: isString(environmentId)
  });
};

export const useGetRunsUsage = () => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'runs-usage'],
    queryFn: () => apiClient.environments.getRunsUsage(orgId),
    enabled: !!orgId
  });
};

export const useGetEnvironmentNameify = (environmentId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment-nameify', environmentId],
    queryFn: () =>
      apiClient.environments.getEnvironment(environmentId!, {
        hideNotification: true,
        params: { fields: 'name,projectId' }
      }),
    staleTime: Infinity
  });
};

export const useSearchEnvironment = (searchText: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment-search', searchText],
    queryFn: () =>
      apiClient.environments
        .getAll({
          organizationId: orgId,
          limit: 19,
          searchText,
          onlyMy: false,
          isActive: true
        })
        .then(returnData),
    enabled: !!searchText
  });
};

const environmentsByDriftStatusCacheKey = (
  orgId: string,
  driftStatus: EnvironmentApi.DriftStatus[],
  projectId?: string
) => [orgId, 'environments', 'by-drift-status', { driftStatus: [...driftStatus].sort(), projectId }] as const;

export const useGetEnvironmentsByDriftStatus = ({
  driftStatus,
  projectId
}: {
  driftStatus: EnvironmentApi.DriftStatus[];
  projectId?: string;
}): UseQueryResult<Environment[]> => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: environmentsByDriftStatusCacheKey(orgId, driftStatus, projectId),
    queryFn: () =>
      apiClient.environments
        .getAll({
          ...(projectId ? { projectId } : { organizationId: orgId }),
          driftStatus,
          onlyMy: false,
          isActive: true
        })
        .then(returnData),
    enabled: Boolean(orgId)
  });
};

export const useGetLatestDriftDeployment = (environmentId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment', environmentId, 'latest-drift'],
    queryFn: () =>
      apiClient.environments
        .getEnvironmentDeployments(environmentId)
        .then(response =>
          response.data.find(deployment => deployment.type === 'driftDetection' && deployment.status === 'SUCCESS')
        )
  });
};

export const useGetDriftedResourceEvents = ({
  enabled = true,
  deploymentId,
  resourceType,
  resourceId
}: {
  enabled: boolean;
  deploymentId: string;
  resourceType?: string;
  resourceId?: string;
}) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['cloud-resources', 'drift-blame-events', { deploymentId, resourceType, resourceId }],
    enabled: enabled && !!resourceType && !!resourceId,
    queryFn: async () => {
      return apiClient.environments.getDriftedResourceEvents(deploymentId, resourceType!, resourceId!);
    }
  });
};
